/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from 'react'
import { getBrands, getFilterMap } from '../api'
import BrandCard from '../components/BrandCard'
import './AllBrands.css'
import SuggestBrandModal from '../components/SuggestBrandModal'

function MultiSelectDropdown({ label, options, selected, onChange }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const toggle = (val) => {
        if (selected.includes(val)) onChange(selected.filter(v => v !== val))
        else onChange([...selected, val])
    }

    return (
        <div className="ab-multiselect" ref={ref}>
            <button
                type="button"
                className={`ab-trigger ${selected.length ? 'has-selection' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                {label}
                <span className="ab-arrow">▾</span>
            </button>
            {open && (
                <div className="ab-menu">
                    {options.map(opt => (
                        <label key={opt.value} className="ab-option">
                            <input
                                type="checkbox"
                                checked={selected.includes(opt.value)}
                                onChange={() => toggle(opt.value)}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    )
}

function CountryDropdown({ countries, selected, onChange }) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const ref = useRef(null)

    useEffect(() => {
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const toggle = (val) => {
        if (selected.includes(val)) onChange(selected.filter(v => v !== val))
        else onChange([...selected, val])
    }

    const filtered = query.trim()
        ? countries.filter(c => c.toLowerCase().includes(query.toLowerCase()))
        : countries

    return (
        <div className="ab-multiselect" ref={ref}>
            <button
                type="button"
                className={`ab-trigger ${selected.length ? 'has-selection' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                Country
                <span className="ab-arrow">▾</span>
            </button>
            {open && (
                <div className="ab-menu">
                    <div className="ab-search-wrap">
                        <input
                            type="text"
                            className="ab-search"
                            placeholder="Search countries..."
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {filtered.map(c => (
                        <label key={c} className="ab-option">
                            <input
                                type="checkbox"
                                checked={selected.includes(c)}
                                onChange={() => toggle(c)}
                            />
                            {c}
                        </label>
                    ))}
                    {filtered.length === 0 && (
                        <div className="ab-empty">No countries match</div>
                    )}
                </div>
            )}
        </div>
    )
}

function AllBrands() {
    const [brands, setBrands] = useState([])
    const [filtered, setFiltered] = useState([])
    const [filterGroups, setFilterGroups] = useState({})
    const [selectedThemes, setSelectedThemes] = useState([])
    const [selectedPrices, setSelectedPrices] = useState([])
    const [selectedCountries, setSelectedCountries] = useState([])
    const [search, setSearch] = useState('')
    const [showSuggest, setShowSuggest] = useState(false)

    useEffect(() => {
        getBrands().then(data => {
            setBrands(data || [])
            setFiltered(data || [])
        })
        getFilterMap().then(data => setFilterGroups(data || {}))
    }, [])

    useEffect(() => {
        let result = [...brands]

        if (selectedThemes.length) {
            result = result.filter(b =>
                selectedThemes.some(theme => {
                    const tags = filterGroups[theme] || []
                    return tags.includes(b.cultural_origin_primary) || tags.includes(b.cultural_origin_secondary)
                })
            )
        }

        if (selectedPrices.length) {
            result = result.filter(b => selectedPrices.includes(b.price_range))
        }

        if (selectedCountries.length) {
            result = result.filter(b => selectedCountries.includes(b.country))
        }

        if (search.trim()) {
            result = result.filter(b =>
                b.name.toLowerCase().includes(search.toLowerCase())
            )
        }

        setFiltered(result)
    }, [selectedThemes, selectedPrices, selectedCountries, search, brands, filterGroups])

    const themeOptions = Object.keys(filterGroups).sort().map(g => ({ label: g, value: g }))
    const priceOptions = [
        { label: '$', value: '1' },
        { label: '$$', value: '2' },
        { label: '$$$', value: '3' },
        { label: '$$$$', value: '4' },
        { label: '$$$$$', value: '5' },
    ]
    const countries = [...new Set(brands.map(b => b.country).filter(Boolean))].sort()

    const hasActiveFilters = selectedThemes.length || selectedPrices.length || selectedCountries.length || search.trim()

    const resetFilters = () => {
        setSelectedThemes([])
        setSelectedPrices([])
        setSelectedCountries([])
        setSearch('')
    }

    return (
        <div className="all-brands-page">
            <div className="page-header">
                <h1>All Brands</h1>
                <p>{filtered.length} brands in the index</p>
            </div>

            <div className="filters">
                <input
                    type="text"
                    placeholder="Search brands..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="search-input"
                />
                {search.trim() && filtered.length < 4 && (
                    <button
                        type="button"
                        className="suggest-search-hint"
                        onClick={() => setShowSuggest(true)}
                    >
                        Don't see "{search.trim()}"? Suggest it
                    </button>
                )}
                <div className="ab-filter-row">
                    <MultiSelectDropdown
                        label="Theme"
                        options={themeOptions}
                        selected={selectedThemes}
                        onChange={setSelectedThemes}
                    />
                    <MultiSelectDropdown
                        label="Price"
                        options={priceOptions}
                        selected={selectedPrices}
                        onChange={setSelectedPrices}
                    />
                    <CountryDropdown
                        countries={countries}
                        selected={selectedCountries}
                        onChange={setSelectedCountries}
                    />
                    <button
                        type="button"
                        className={`ab-reset${hasActiveFilters ? '' : ' ab-reset--inactive'}`}
                        onClick={resetFilters}
                        disabled={!hasActiveFilters}
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="brand-grid">
                {filtered.map(brand => (
                    <BrandCard key={brand.id} brand={brand} showSimilar={true} />
                ))}
            </div>
        {showSuggest && (
                <SuggestBrandModal
                    initialBrandName={search}
                    onClose={() => setShowSuggest(false)}
                />
            )}
        </div>
    )
}

export default AllBrands
